import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'gatsby';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Layout } from '../index';
import Pharmacy from './Pharmacy';
import { PharmacyStatistics } from '../../pages/index';
import { canonicalUrl, withTrailingSlash } from '../../utils/canonicalUrl';

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(12),
  },
  linkArrow: {
    float: 'left',
    color: 'rgba(0, 0, 0, 0.87);',
  },
  linkText: {
    fontSize: '20px',
    lineHeight: '24px',
    color: 'rgba(0, 0, 0, 0.87);',
    marginLeft: '30px',
    marginBottom: theme.spacing(0),
  },
  backToVerkkoapteekitLink: {
    textDecoration: 'none',
    clear: 'both',
  },
  intro: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
    lineHeight: 1.7,
  },
  introLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none !important',
    '&:hover': { textDecoration: 'underline !important' },
  },
}));

type PageContext = {
  city: string;
  pharmacies: PharmacyStatistics[];
};

type Props = {
  pageContext: PageContext;
};

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/å/g, 'a')
    .replace(/\s+/g, '-');

const CityPage: React.FunctionComponent<Props> = ({ pageContext }) => {
  const classes = useStyles();
  const { city, pharmacies } = pageContext;
  const citySlug = toSlug(city);

  return (
    <>
      <Helmet>
        <title>{city} verkkoapteekit | Treet-verkkoapteekki</title>
        <meta
          name="description"
          content={`Treet verkkoapteekit ${city}ssa. Tilaa reseptilääkkeitä ja apteekkituotteita turvallisesti netistä.`}
        />
        <link rel="canonical" href={canonicalUrl(`/verkkoapteekit/${citySlug}`)} />
      </Helmet>
      <Layout>
        <Container className={classes.container} maxWidth="lg">
          <Link to={withTrailingSlash('/verkkoapteekit')} className={classes.backToVerkkoapteekitLink}>
            <ArrowBackIcon className={classes.linkArrow} />
            <Typography className={classes.linkText}>Takaisin verkkoapteekkeihin</Typography>
          </Link>

          <Typography variant="body1" className={classes.intro}>
            Tässä on kaikki kaupungissa {city} toimivat verkkoapteekit, joista voit ostaa apteekin valikoimaan kuuluvia
            tuotteita kuten apteekkikosmetiikkaa, itsehoitolääkkeitä ja reseptilääkkeitä. Etsi lähin apteekkisi{' '}
            <a
              href="https://app.treet.fi/pharmacy-search"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.introLink}
            >
              täältä
            </a>{' '}
            tai valitse haluamasi apteekki suoraan alla olevasta listasta.
          </Typography>
          <Pharmacy pharmacies={pharmacies} />
        </Container>
      </Layout>
    </>
  );
};

export default CityPage;
